import { clientEnv } from "../env";

/** A preview is not an accepted user turn and must never enter chat history. */
export type AnticipationMode = "typing" | "continuation";
export interface AnticipationTurn {
  role: "user" | "assistant";
  content: string;
}
export interface AnticipationRequest {
  mode: AnticipationMode;
  draft: string;
  history: AnticipationTurn[];
  language: "es" | "en";
  sessionToken: string;
  signal?: AbortSignal;
}
export interface AnticipationReply {
  status: "ready" | "skipped" | "unavailable" | "busy";
  mode: AnticipationMode;
  preview?: string;
  provisional: true;
  credits: 0;
}

export async function requestOmniAnticipation(input: AnticipationRequest): Promise<AnticipationReply> {
  if (!input.sessionToken) throw new Error("Falta la sesión de OMNISYNIA.");
  const signal = input.signal
    ? AbortSignal.any([input.signal, AbortSignal.timeout(8_000)])
    : AbortSignal.timeout(8_000);
  const response = await fetch(clientEnv.apiBaseUrl + "/ai/anticipate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + input.sessionToken
    },
    signal,
    body: JSON.stringify({
      mode: input.mode, draft: input.draft,
      history: input.history.slice(-8).map(item => ({
        role: item.role, content: item.content.slice(0, 1000)
      })),
      language: input.language
    })
  });
  if (!response.ok) throw new Error("ONE CORE no pudo preparar la anticipación.");
  const data: unknown = await response.json();
  if (!data || typeof data !== "object" || Array.isArray(data))
    throw new Error("La anticipación devolvió datos inválidos.");
  const result = data as Record<string, unknown>;
  if (!["ready", "skipped", "unavailable", "busy"].includes(String(result.status)) ||
      result.provisional !== true || result.credits !== 0 ||
      result.mode !== input.mode) throw new Error("Respuesta de anticipación no válida.");
  const status = result.status as AnticipationReply["status"];
  if (status === "ready" &&
      (typeof result.preview !== "string" || result.preview.length < 8 || result.preview.length > 340))
    throw new Error("Vista previa no válida.");
  return {
    status, mode: input.mode, provisional: true, credits: 0,
    ...(status === "ready" ? { preview: String(result.preview) } : {})
  };
}
